"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";
import { DOCS_HUB, docGroups, type DocGate } from "@/lib/docs/catalog";

/**
 * The docs navigation, rendered from the catalog. A client island only
 * because the active state needs the pathname; the data is static and the
 * group labels are `Docs.groups.*`.
 */
export function DocsSidebar({ className }: { className?: string }) {
  const pathname = usePathname();
  const t = useTranslations("Docs");

  const link = (href: string, title: string, gated?: DocGate) => {
    const active = pathname === href;
    return (
      <Link
        href={href}
        aria-current={active ? "page" : undefined}
        className={cn(
          "flex min-h-11 items-center justify-between gap-2 rounded-md px-3 py-1.5 text-sm transition-colors",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          active ? "bg-accent font-medium text-accent-foreground" : "text-muted-foreground hover:bg-accent/60 hover:text-foreground",
        )}
      >
        <span className="line-clamp-2">{title}</span>
        {gated ? <GatedPill gate={gated} /> : null}
      </Link>
    );
  };

  return (
    <nav aria-label={t("navigation")} className={cn("flex flex-col gap-6", className)}>
      {docGroups.map((group) => (
        <div key={group.id}>
          <p className="mb-2 px-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t(`groups.${group.id}`)}</p>
          <ul className="flex flex-col gap-0.5">
            {group.id === "startHere" ? <li>{link(DOCS_HUB.href, t("overview"))}</li> : null}
            {group.pages.map((page) => (
              <li key={page.slug}>{link(page.href, page.title, page.gated)}</li>
            ))}
          </ul>
        </div>
      ))}
    </nav>
  );
}

function GatedPill({ gate }: { gate: DocGate }) {
  const t = useTranslations("Docs");
  const label = "plan" in gate ? t("gatedPlan", { plan: gate.plan }) : t("gatedFlag");
  return (
    <span
      title={label}
      aria-label={label}
      data-testid="doc-gated-pill"
      className="shrink-0 rounded-full border border-border bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground"
    >
      {"plan" in gate ? gate.plan : t("gatedShort")}
    </span>
  );
}
